/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getJob,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const setJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
    value: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, jobId, savedName, value} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (job != null) {
    job.parameters ??= {}
    job.parameters[savedName] = value
  }
}

export const renameJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    name: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, savedName, name} = action.payload

  if (name !== savedName) {
    const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)
    pipeline.renamed ??= {}
    pipeline.renamed!.jobParameters ??= {}
    pipeline.renamed!.jobParameters![savedName] = name
  } else if (
    getPipelineDraftEntry(state, pipelineId, branch)?.renamed?.jobParameters?.[savedName] != null
  ) {
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    delete pipeline.renamed!.jobParameters![savedName]
    deleteIfEmpty(pipeline.renamed, 'jobParameters') && deleteIfEmpty(pipeline, 'renamed')
  }
}

export const deleteJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, jobId, savedName} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (getPipelineDraftEntry(state, pipelineId, branch)?.added?.jobParameters?.[savedName]) {
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    delete job?.parameters?.[savedName]
    delete pipeline.added!.jobParameters![savedName]
    deleteIfEmpty(pipeline.added, 'jobParameters') && deleteIfEmpty(pipeline, 'added')
    delete pipeline.renamed!.jobParameters![savedName]
    deleteIfEmpty(pipeline.renamed, 'jobParameters') && deleteIfEmpty(pipeline, 'renamed')
  } else {
    const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)
    pipeline.deleted ??= {}
    pipeline.deleted!.jobParameters ??= []
    pipeline.deleted!.jobParameters!.push(savedName)
  }
}

export const restoreJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; savedName: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, savedName} = action.payload
  const {deleted} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}

  if (deleted?.jobParameters?.includes(savedName)) {
    deleted.jobParameters = deleted?.jobParameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'jobParameters') &&
      deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'deleted')
  }
}

export const addJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; prefix?: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, prefix = '', jobId} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (job != null) {
    job.parameters ??= {}
    const key = generateFreeName({
      getName: iteration => `parameter_${jobId}_${iteration}`,
      usedNames: Object.keys(job.parameters),
      errorMessage: 'Cannot generate unique parameter key',
    })
    job.parameters[key] = ''
    renameJobParameter(state, {
      type: '',
      payload: {pipelineId, name: prefix, savedName: key, branch},
    })
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    pipeline.added ??= {}
    pipeline.added!.jobParameters ??= {}
    pipeline.added!.jobParameters![key] = true
  }
}

export const clearAddedJobParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, jobId} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  const job = getJob(state, pipelineId, jobId, branch)

  if (added?.jobParameters != null && job != null) {
    Object.keys(added.jobParameters).forEach(key => {
      delete job.parameters?.[key]
    })
    delete added.jobParameters
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}

export const applyAddedJobParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; branch: string}>
> = (state, action) => {
  const {pipelineId, branch} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}

  if (added?.jobParameters != null) {
    delete added.jobParameters
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}
